/**
 * Home do Cabal Next (mockup do dono, 2026-10-10: website-mockup-updated-screenshots.png).
 * Header -> hero em tela cheia (arte home.jpg) -> ultimas noticias (NewsSection) -> "Jogue no PC ou Android" (cards com as telas de gameplay) ->
 * faixa do download (bg.png; fase beta: downloads "Em breve") -> rodape. Cards, botoes e icones sao elementos do site (o mockup e' so'
 * referencia); a arte vem de public/assets/next/. CSS isolado em next-home.css (prefixo nx-).
 * Efeitos: rolagem suave, item do menu acompanha a secao na tela (barra na borda do header), entrada do hero,
 * secoes aparecem ao rolar (data-reveal) -- tudo desligado com "reduzir movimento" do sistema.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Gamepad2,
  Gem,
  Globe,
  Monitor,
  MonitorSmartphone,
  RefreshCw,
  Rocket,
  Target,
  Ticket,
  Users,
  Zap,
} from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useClientDownload } from '@/shared/hooks/useClientDownload';
import { BetaSignup } from './BetaSignup';
import { AndroidIcon, WindowsIcon } from './icons';
import { NewsSection } from './NewsSection';
import { ASSET, HEADER_HEIGHT, NAV, NextFooter, NextHeader, scroller, useReveal } from './NextLayout';
import { NextLogin } from './NextLogin';

/**
 * Item do menu ativo. Rolando: a ultima secao cujo topo passou de uma linha a ~35% da tela (no fim da pagina,
 * vale a ultima secao visivel). Clique no menu: o item clicado fica fixo ate' o usuario rolar por conta propria
 * (roda/toque/teclado) -- a pagina e' curta (o hero ocupa a tela), entao "Sobre" ja' chega no fim da pagina.
 */
function useActiveSection() {
  const [active, setActive] = useState(NAV[0].id);
  const pinned = useRef<string | null>(null);
  useEffect(() => {
    const el = scroller();
    let frame = 0;
    const update = () => {
      frame = 0;
      if (pinned.current) {
        setActive(pinned.current);
        return;
      }
      const probe = HEADER_HEIGHT + (el.clientHeight - HEADER_HEIGHT) * 0.35;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      let current = NAV[0].id;
      for (const item of NAV) {
        const top = document.getElementById(item.id)?.getBoundingClientRect().top;
        if (top !== undefined && (top <= probe || (atBottom && top < el.clientHeight))) current = item.id;
      }
      // menu so' tem Inicio/Noticias (fase beta): rolou pra baixo do fim da secao -> nenhum item aceso
      const bottom = document.getElementById(current)?.getBoundingClientRect().bottom;
      if (bottom !== undefined && bottom <= probe) current = '';
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const unpin = () => {
      pinned.current = null;
      onScroll();
    };
    update();
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', unpin, { passive: true });
    el.addEventListener('touchstart', unpin, { passive: true });
    window.addEventListener('keydown', unpin);
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', unpin);
      el.removeEventListener('touchstart', unpin);
      window.removeEventListener('keydown', unpin);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  const pin = useCallback((id: string) => {
    pinned.current = id;
    setActive(id);
  }, []);
  return [active, pin] as const;
}

// Downloads (dono, 2026-10-20): os 2 links vem do painel (Admin > Download). Link vazio = cinza com o selo "Em breve";
// com link vira botao. So' baixa quem esta' logado: deslogado, o clique abre o LOGIN (dono, 2026-10-24: era a
// inscricao do beta; o login deixa claro que e' ele que libera o download).
function DownloadButtons() {
  const { data } = useClientDownload();
  const { isLoggedIn } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const items = [
    {
      key: 'pc',
      className: 'nx-dl--pc',
      title: 'Download PC',
      sub: 'Windows | Sem emulador',
      icon: <WindowsIcon size={34} className="nx-dl__icon" />,
      url: data?.url?.trim() || '',
    },
    {
      key: 'android',
      className: 'nx-dl--android',
      title: 'Download Android',
      sub: 'APK direto no site',
      icon: <AndroidIcon size={36} className="nx-dl__icon" />,
      url: data?.android?.url?.trim() || '',
    },
  ];
  return (
    <div className="nx-downloads">
      {items.map((item) => {
        const body = (
          <>
            {item.icon}
            <span className="nx-dl__text">
              <strong>{item.title}</strong>
              <small>{item.sub}</small>
            </span>
          </>
        );
        if (!item.url)
          return (
            <span key={item.key} className={`nx-dl ${item.className} is-soon`} aria-disabled="true" title="Em breve">
              {body}
              <em className="nx-dl__soon">Em breve</em>
            </span>
          );
        return (
          <a
            key={item.key}
            className={`nx-dl ${item.className}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (isLoggedIn) return;
              e.preventDefault();
              setLoginOpen(true);
            }}
          >
            {body}
          </a>
        );
      })}
      <NextLogin open={loginOpen} onClose={() => setLoginOpen(false)} onSignup={() => setSignupOpen(true)} />
      <BetaSignup open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  );
}

function Hero() {
  return (
    <section id="inicio" className="nx-hero">
      <div className="nx-hero__art" style={{ backgroundImage: `url(${ASSET}/hero.webp)` }} />
      <div className="nx-container nx-hero__inner">
        <div className="nx-hero__copy">
          <p className="nx-eyebrow nx-hero__eyebrow nx-in" style={{ '--d': '0.05s' } as CSSProperties}>
            Jogue como quiser. Onde quiser.
          </p>
          <h1 className="nx-hero__title nx-in" style={{ '--d': '0.15s' } as CSSProperties}>
            <span className="nx-hero__title-1">Revolucionário</span>
            <span className="nx-hero__title-2">e sem limites.</span>
          </h1>
          <p className="nx-hero__text nx-in" style={{ '--d': '0.3s' } as CSSProperties}>
            O clássico MMORPG que você ama, agora no seu celular e no seu PC, sem emulador. Duas versões, um só mundo.
            Escolha sua forma de jogar e viva a experiência Cabal como nunca antes.
          </p>
          <div className="nx-in" style={{ '--d': '0.45s' } as CSSProperties}>
            <DownloadButtons />
          </div>
          <ul className="nx-perks nx-in" style={{ '--d': '0.6s' } as CSSProperties}>
            <li>
              <Zap className="nx-perks__icon" />
              <span>
                <strong>Sem emulador</strong>
                <small>Jogue nativo, sem complicação</small>
              </span>
            </li>
            <li>
              <MonitorSmartphone className="nx-perks__icon" />
              <span>
                <strong>PC + Mobile</strong>
                <small>Duas versões, mesma conta</small>
              </span>
            </li>
            <li>
              <Globe className="nx-perks__icon" />
              <span>
                <strong>Jogue onde quiser</strong>
                <small>No seu tempo, do seu jeito</small>
              </span>
            </li>
          </ul>
        </div>
      </div>
      <a className="nx-hero__scroll" href="#noticias" aria-label="Ver mais">
        <ChevronDown size={26} />
      </a>
    </section>
  );
}

// Dono (2026-10-10): textos na 1a linha e as duas telas divididas na 2a, com mais espaco/enfase
function Versions() {
  return (
    <section id="sobre" className="nx-versions">
      <div className="nx-container">
        <div className="nx-versions__intro">
          <div className="nx-versions__copy" data-reveal>
            <p className="nx-eyebrow">Duas versões, a mesma experiência</p>
            <h2 className="nx-title">
              Jogue no PC ou Android
              <span className="nx-title__accent">Sem emulador</span>
            </h2>
          </div>
          <div className="nx-versions__side" data-reveal style={{ '--d': '0.1s' } as CSSProperties}>
            <p className="nx-text">
              Aproveite o melhor dos dois mundos! No PC, você tem mais precisão e conforto, enquanto no mobile a
              liberdade de jogar onde e quando quiser é total.
            </p>
            <div className="nx-platforms">
              <div className="nx-platform">
                <Monitor className="nx-platform__icon" />
                <div>
                  <strong>PC Windows</strong>
                  <ul>
                    <li>Mais desempenho</li>
                    <li>Gráficos otimizados</li>
                    <li>Controle completo</li>
                  </ul>
                </div>
              </div>
              <div className="nx-platform">
                <AndroidIcon size={40} className="nx-platform__icon nx-platform__icon--android" />
                <div>
                  <strong>Android</strong>
                  <ul>
                    <li>APK direto no site</li>
                    <li>Instalação simples</li>
                    <li>Jogue em qualquer lugar</li>
                  </ul>
                </div>
              </div>
            </div>
            <a className="nx-outline-btn" href="#download">
              Conheça as versões <ChevronRight size={16} />
            </a>
          </div>
        </div>

        <div className="nx-versions__media">
          <div className="nx-shots">
            <figure className="nx-shot nx-shot--pc" data-reveal style={{ '--d': '0.1s' } as CSSProperties}>
              <figcaption className="nx-shot__tab">
                <WindowsIcon size={16} /> PC - Windows
              </figcaption>
              <div className="nx-shot__frame">
                <img src={`${ASSET}/shot-pc.webp`} alt="Cabal Next no PC" loading="lazy" />
              </div>
            </figure>
            <figure className="nx-shot nx-shot--android" data-reveal style={{ '--d': '0.25s' } as CSSProperties}>
              <figcaption className="nx-shot__tab">
                <AndroidIcon size={18} /> Android - APK
              </figcaption>
              <div className="nx-shot__frame">
                <img src={`${ASSET}/shot-mobile.webp`} alt="Cabal Next no Android" loading="lazy" />
              </div>
            </figure>
          </div>
          <ul id="recursos" className="nx-features">
            <li data-reveal style={{ '--d': '0.1s' } as CSSProperties}>
              <Target className="nx-features__icon" />
              <span>
                <strong>Gráficos incríveis</strong>
                <small>Mesmo mundo, melhor visual.</small>
              </span>
            </li>
            <li id="classes" data-reveal style={{ '--d': '0.2s' } as CSSProperties}>
              <Users className="nx-features__icon" />
              <span>
                <strong>9 classes originais</strong>
                <small>Escolha seu estilo de luta.</small>
              </span>
            </li>
            <li data-reveal style={{ '--d': '0.3s' } as CSSProperties}>
              <Gem className="nx-features__icon" />
              <span>
                <strong>Sistema completo</strong>
                <small>Itens, Craft, Alz, DGs, World Boss.</small>
              </span>
            </li>
            <li data-reveal style={{ '--d': '0.4s' } as CSSProperties}>
              <CalendarDays className="nx-features__icon" />
              <span>
                <strong>Eventos diários</strong>
                <small>Jogue com amigos.</small>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function DownloadBand() {
  return (
    <section id="download" className="nx-band" style={{ backgroundImage: `url(${ASSET}/band.webp)` }}>
      <div className="nx-container nx-band__inner">
        <div className="nx-band__center" data-reveal>
          <p className="nx-eyebrow">Seu Cabal. Do seu jeito.</p>
          <h2 className="nx-title nx-title--center">
            Prepare-se para entrar
            <span className="nx-title__accent">nessa aventura</span>
          </h2>
          <div className="nx-band__actions">
            <DownloadButtons />
            <p className="nx-script">
              jogue onde
              <br />
              você quiser!
              <svg className="nx-script__swoosh" viewBox="0 0 120 16" aria-hidden="true">
                <path d="M2 13 C 40 4, 80 2, 118 6" />
              </svg>
            </p>
          </div>
        </div>
        <ul id="suporte" className="nx-band__list" data-reveal style={{ '--d': '0.2s' } as CSSProperties}>
          <li>
            <span className="nx-ring">
              <Gamepad2 size={18} />
            </span>
            Sem emulador
          </li>
          <li>
            <span className="nx-ring">
              <RefreshCw size={18} />
            </span>
            Crossplatform
          </li>
          <li>
            <span className="nx-ring">
              <Rocket size={18} />
            </span>
            Beta nos próximos dias
          </li>
          <li>
            <span className="nx-ring">
              <Ticket size={18} />
            </span>
            Inscrição gratuita
          </li>
        </ul>
      </div>
    </section>
  );
}

export default function NextHome() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [active, pinSection] = useActiveSection();
  const location = useLocation();
  useReveal(rootRef);
  useEffect(() => {
    document.title = 'Cabal Next';
  }, []);
  // vindo de outra pagina com "/#secao" (menu do /news): o router nao rola sozinho ate a ancora
  useEffect(() => {
    const id = location.hash.slice(1);
    const target = id ? document.getElementById(id) : null;
    if (target) {
      pinSection(id);
      requestAnimationFrame(() => target.scrollIntoView({ behavior: 'instant' as ScrollBehavior }));
    } else {
      scroller().scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [location.hash, pinSection]);
  return (
    <div ref={rootRef} className="nx">
      <NextHeader active={active} onHome onNavigate={pinSection} />
      <main>
        <Hero />
        <NewsSection />
        <Versions />
        <DownloadBand />
      </main>
      <NextFooter />
    </div>
  );
}
